import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { Alert, Box, Typography, TextField, useTheme } from "@mui/material";
import {
  PamGrid,
  useNotification,
  useRowSelection,
  ConfirmationDialog,
  SummaryStatsBar,
  useSummaryStats,
  formatDate,
  formatDateTimeForDisplay,
  isValueNull,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
  useOptionalPermissionCenter,
  getWriteErrorMessage,
  shouldReloadAfterWriteError,
} from "@1823-partners/core";
import { SetFilterModule } from "ag-grid-enterprise";

import api, { fetchReconEntries, saveReconEntries, fetchReconDates, saveExclusions } from "../api";
import config from "../data/config.json";
import {
  DuplicateCommentRowsError,
  buildCommentWrites,
  commentKey,
  reapplyCommentEdits,
  recordCommentEdit,
  removeSavedEdits,
  withRowIds,
} from "../utils/commentEdits";
import { unavailableReason } from "../config/backendFeatures";
import {
  filterBreaks,
  countBreaks,
  countWhere,
  isOrigUnitBreak,
  isCurrUnitBreak,
  isPersistentBreak,
  isNotMatching,
} from "../utils/breakStats";

/*
 * The server gate behind the Exclude button (see recon_server's exclusions route). Named here so
 * the button can say why it is disabled instead of letting the click fail with a 403 toast.
 *
 * The server remains the enforcer -- this is a hint, and it is deliberately a PERMISSIVE one: an
 * unmounted center, a still-loading fetch or a failed one must leave the button enabled. Disabling
 * on absent information would lock out a user who actually holds the grant, which is worse than
 * the 403 this is meant to pre-empt.
 */
const EXCLUSIONS_WRITE = 'recon.exclusions:write';


// RECON.EXCLUDED_POSITIONS key, as the recon grid names its columns. The
// backend keys exclusions by CUSTODIAN, ACCOUNT_ID, SOURCE_ACCOUNT, IDENTIFIER
// and ASOFDT; CUSTODY_IDENTIFIER (CU_SEC_ID) is carried, not keyed.
const exclusionKey = (row, selectedDate) => JSON.stringify(
  [row.REC_CUST, row.CW_ACCT_ID, row.REC_SRC_ACCT, row.REC_SEC_ID, row.REC_ASOFDT || selectedDate]
    .map(value => (value === undefined ? null : value))
);

// Row ID for the grid and selection: unique per loaded row (see withRowIds).
const getReconRowId = (data) =>
  data.__rowId ?? `${data.REC_ASOFDT || ''}_${data.CW_ACCT_ID || ''}_${data.REC_CUST || ''}_${data.REC_SRC_ACCT || ''}_${data.REC_SEC_ID || ''}_${data.CU_SEC_ID || ''}`;

/**
 * `onUnsavedChange` reports whether the grid is holding comment edits that have
 * not been saved. The Fixed Income perspective switch unmounts this component,
 * which would discard them silently, so it asks first.
 */
const ReconList = ({
  onSelectEntry, onSaveSuccess, onExclusionSuccess, userId, height = 500, onUnsavedChange,
}) => {
  const [quickFilterText, setQuickFilterText] = useState('');
  const [showBreaksOnly, setShowBreaksOnly] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExcluding, setIsExcluding] = useState(false);
  // An OPEN editor counts as unsaved, not just a committed edit. PamGrid sets
  // stopEditingWhenCellsLoseFocus, so clicking away normally commits first — but
  // the perspective switch must not depend on that ordering to notice a comment
  // someone is halfway through typing.
  const [isEditingCell, setIsEditingCell] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [exclusionReason, setExclusionReason] = useState('');
  const [exclusionCoverage, setExclusionCoverage] = useState(0);
  const commentEditsRef = useRef(new Map());
  const gridRef = useRef(null);
  const theme = useTheme();
  const { showNotification } = useNotification();
  // Optional, not usePermissionCenter: this grid is rendered without a provider in several tests,
  // and the throwing hook would turn a missing provider into a crash rather than a lost hint.
  const permissionCenter = useOptionalPermissionCenter();
  // Writes the current backend does not serve (src/config/backendFeatures.js).
  const COMMENTS_UNAVAILABLE = unavailableReason('comments');
  const EXCLUSIONS_UNAVAILABLE = unavailableReason('exclusions');
  const canExclude =
    !permissionCenter || permissionCenter.loading || permissionCenter.error
      ? true
      : permissionCenter.hasPermission(EXCLUSIONS_WRITE);

  // Use 1823-core row selection hook
  const {
    selectedRows,
    setSelectedRows,
    clearSelection,
    selectedCount,
    getSelectedData,
  } = useRowSelection({ getRowId: getReconRowId });

  const loadReconDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      const response = await fetchReconDates();
      const dates = Array.isArray(response.data) ? response.data : [];
      setAvailableDates(dates);

      if (!selectedDate && dates.length > 0) {
        const sortedDates = [...dates].sort((a, b) => new Date(b) - new Date(a));
        setSelectedDate(sortedDates[0]);
      }
    } catch (error) {
      console.error("Error fetching recon dates:", error);
      // Permission denial / outside-org: surface guidance (with a Request
      // access button), do NOT fabricate fallback dates.
      if (!notifyPermissionError(showNotification, error)) {
        // Surface the failure; never fabricate dates that map to no real
        // recon data (which would silently mislead the user).
        showNotification(error?.message || 'Failed to load recon dates', 'error');
        setAvailableDates([]);
      }
    } finally {
      setIsLoadingDates(false);
    }
  }, [selectedDate, showNotification]);

  // Only the newest load may touch state. Without this, a slow answer for an earlier date lands
  // after the newer one and shows that date's positions (and exports them) under the new date.
  const loadSeqRef = useRef(0);
  // The date whose rows are on screen, so a failed load can tell "same date, keep the drafts"
  // from "another date, do not leave its rows up".
  const loadedDateRef = useRef(null);
  // The date the user has selected right now. A write that finishes after the user moved on
  // reloads with the date it started on; that reload must not start, because it would supersede
  // the new date's own load and then put the old date's rows under the new one. Written in a
  // layout effect, not during render: React can discard a render, and a date that never
  // committed must not make a reload for the date that did commit skip itself. A layout effect
  // runs before the passive effect below that loads the new date, so that load sees it.
  const selectedDateRef = useRef(selectedDate);
  useLayoutEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);
  // True after a reload failed with rows still on screen. Those rows may predate a write that
  // landed, so they carry out-of-date tokens; comments stay read-only until a reload succeeds.
  const [rowsStale, setRowsStale] = useState(false);

  const loadEntries = useCallback(async ({ preserveEdits = false } = {}) => {
    if (selectedDate !== selectedDateRef.current) return;
    const seq = ++loadSeqRef.current;
    const isCurrent = () => seq === loadSeqRef.current;
    try {
      setIsLoading(true);
      const response = await fetchReconEntries(selectedDate);
      if (!isCurrent()) return;
      const formattedData = Array.isArray(response.data)
        ? withRowIds(response.data).map(entry => ({
          ...entry,
          REC_ASOFDT: entry.REC_ASOFDT ? formatDate(entry.REC_ASOFDT) : entry.REC_ASOFDT,
          CW_AODT: entry.CW_AODT ? formatDate(entry.CW_AODT) : entry.CW_AODT,
          CU_AODT: entry.CU_AODT ? formatDate(entry.CU_AODT) : entry.CU_AODT,
        }))
        : response.data;
      const restored = preserveEdits && Array.isArray(formattedData)
        ? reapplyCommentEdits(formattedData, commentEditsRef.current)
        : { rows: formattedData, edits: new Map() };
      commentEditsRef.current = restored.edits;
      loadedDateRef.current = selectedDate;
      setRowsStale(false);
      setRowData(restored.rows);
      setHasUnsavedChanges(restored.edits.size > 0);
      clearSelection();
      if (restored.conflicts) {
        showNotification(
          `${restored.conflicts} comment(s) you were editing were changed or removed by someone else, so your edits to them were discarded and the saved version is shown. Re-enter yours if still needed.`,
          'warning'
        );
      }
    } catch (error) {
      if (!isCurrent()) return;
      // A failed reload of the same date keeps its rows, and the drafts on them, but marks them
      // stale. Rows that belong to another date must not stay up under this one.
      const keepsRows = loadedDateRef.current === selectedDate;
      if (keepsRows) {
        setRowsStale(true);
      } else {
        loadedDateRef.current = null;
        setRowData([]);
        setRowsStale(false);
      }
      console.error("Error fetching recon entries:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(
          keepsRows
            ? 'Failed to reload recon entries. Comments are read-only until a reload succeeds; use Reload.'
            : 'Failed to load recon entries. Please try again.',
          'error'
        );
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [selectedDate, clearSelection, showNotification]);

  useEffect(() => {
    loadReconDates();
  }, [loadReconDates]);

  useEffect(() => {
    if (selectedDate) {
      loadEntries();
    }
  }, [selectedDate, loadEntries]);

  useEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges || isEditingCell);
  }, [hasUnsavedChanges, isEditingCell, onUnsavedChange]);

  const handleDateChange = useCallback((newDate) => {
    if (commentEditsRef.current.size > 0) {
      showNotification('Save or undo your comment edits before changing the date.', 'warning');
      return;
    }
    setSelectedDate(newDate);
  }, [showNotification]);

  const onCellEditingStarted = useCallback(() => setIsEditingCell(true), []);
  const onCellEditingStopped = useCallback(() => setIsEditingCell(false), []);

  // Create cell style function for alert columns
  const createAlertCellStyle = useCallback((alertField) => {
    return (params) => {
      const alertValue = params.data?.[alertField] ?? params.data?.[alertField.toLowerCase()];
      const alertNum = Number(alertValue);
      const baseStyle = {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider
      };
      if (alertNum === 1 || alertNum === 2) {
        return { ...baseStyle, backgroundColor: 'rgba(255, 165, 0, 0.3)' };
      } else if (alertNum >= 3) {
        // 3 or more consecutive month-ends is "persistent", as breakStats counts it.
        return { ...baseStyle, backgroundColor: 'rgba(255, 0, 0, 0.3)' };
      }
      return baseStyle;
    };
  }, [theme]);

  const columnDefs = useMemo(() => [
    // Pinned columns
    { field: 'REC_CUST', headerName: 'Rec Cust', editable: false, filter: 'agSetColumnFilter', pinned: 'left' },
    { field: 'REC_SRC_ACCT', headerName: 'Rec Account Number', editable: false, filter: 'agSetColumnFilter', pinned: 'left' },
    { field: 'REC_SEC_ID', headerName: 'Rec ID', editable: false, filter: 'agSetColumnFilter', pinned: 'left' },
    // Main columns
    { field: 'CW_SEC_ID', headerName: 'CW ID', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CU_SEC_DES', headerName: 'CUST Des', editable: false, filter: 'agSetColumnFilter', minWidth: 200 },
    { field: 'CW_SEC_DES', headerName: 'CW Des', editable: false, filter: 'agSetColumnFilter', minWidth: 200 },
    { field: 'CW_N_LOT', headerName: 'CW N Lots', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'AT1', headerName: 'Asset Type L1', editable: false, filter: 'agSetColumnFilter' },
    { field: 'AT2', headerName: 'Asset Type L2', editable: false, filter: 'agSetColumnFilter' },
    { field: 'AT3', headerName: 'Asset Type L3', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CW_ORIG_UNITS', headerName: 'CW Orig Units', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'CU_ORIG_UNITS', headerName: 'CUST Orig Units', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'ORIG_UNITS_DIFF', headerName: 'Orig Units Diff', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value, cellStyle: createAlertCellStyle('ORIG_UNITS_ALERT') },
    { field: 'CW_CURR_UNITS', headerName: 'CW Curr Units', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'CU_CURR_UNITS', headerName: 'CUST Curr Units', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'CURR_UNITS_DIFF', headerName: 'Curr Units Diff', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value, cellStyle: createAlertCellStyle('CURR_UNITS_ALERT') },
    { field: 'CW_CV', headerName: 'CW Carry Value', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'CU_ACCT_DES', headerName: 'CUST Account Description', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CU_BV', headerName: 'CUST Book Value', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'CU_BV_REPLACEMENT', headerName: 'CUST BV Replaced', editable: false, filter: 'agSetColumnFilter', cellRenderer: 'agCheckboxCellRenderer', valueGetter: (params) => params.data?.CU_BV_REPLACEMENT === true },
    { field: 'CW_BV', headerName: 'CW Book Value', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'REC_BV_DIFF', headerName: 'BV Diff', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString() : params.value },
    { field: 'REC_BV_ALERT', headerName: 'BV Alert', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CLIENT_SHORT_NAME', headerName: 'CW Account', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CU_CUST', headerName: 'CU Cust', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CW_CUST', headerName: 'CW Cust', editable: false, filter: 'agSetColumnFilter' },
    {
      field: 'CUST_ALERT', headerName: 'Cust Alert', editable: false, filter: 'agSetColumnFilter',
      valueGetter: (params) => {
        const cwCust = params.data?.CW_CUST;
        const cuCust = params.data?.CU_CUST;
        if (cwCust == null || cuCust == null) return 'NOT MATCHING';
        return cwCust === cuCust ? 'MATCHING' : 'NOT MATCHING';
      }
    },
    // Hidden columns
    { field: 'CU_FV', headerName: 'CUST FV', editable: false, type: 'number', filter: 'agNumberColumnFilter', hide: true },
    { field: 'CU_FV_REPLACEMENT', headerName: 'CUST FV Replaced', editable: false, filter: 'agSetColumnFilter', hide: true, cellRenderer: 'agCheckboxCellRenderer', valueGetter: (params) => params.data?.CU_FV_REPLACEMENT === true },
    { field: 'CW_FV', headerName: 'CW FV', editable: false, type: 'number', filter: 'agNumberColumnFilter', hide: true },
    { field: 'REC_FV_DIFF', headerName: 'FV Diff', editable: false, type: 'number', filter: 'agNumberColumnFilter', hide: true },
    { field: 'REC_FV_ALERT', headerName: 'FV Alert', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'REC_ASOFDT', headerName: 'As-of Date', editable: false, type: 'date', filter: 'agDateColumnFilter', hide: true },
    { field: 'CW_ID', headerName: 'PUID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CU_SEC_ID', headerName: 'CUST ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CW_ACCT_ID', headerName: 'CW_ACCT_ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'SEC_ID', headerName: 'SEC_ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CO_ACCT_ID', headerName: 'CO_ACCT_ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CO_CUST', headerName: 'CO_CUST', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CO_SRC_ACCT', headerName: 'CO_SRC_ACCT', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CO_SEC_ID', headerName: 'CO_SEC_ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    { field: 'CO_CUST_ID', headerName: 'CO_CUST_ID', editable: false, filter: 'agSetColumnFilter', hide: true },
    // Visible comment columns
    { field: 'COMMENT_BY', headerName: 'Comment By', editable: false, filter: 'agSetColumnFilter' },
    { field: 'COMMENT_DATE', headerName: 'Comment Date', editable: false, type: 'date', filter: 'agDateColumnFilter', valueFormatter: (params) => formatDateTimeForDisplay(params.value) },
    // Not editable while comment saves are unavailable or a load/save is running,
    // so no edit can be typed and then lost.
    { field: 'COMMENTS', headerName: 'Comments', editable: !COMMENTS_UNAVAILABLE && !isSaving && !isLoading && !rowsStale, pinned: 'right', minWidth: 200 }
  ], [createAlertCellStyle, COMMENTS_UNAVAILABLE, isSaving, isLoading, rowsStale]);

  const onCellValueChanged = useCallback((params) => {
    const { data, api } = params;
    if (params.colDef.field !== 'COMMENTS') {
      setHasUnsavedChanges(true);
      return;
    }
    const value = params.newValue ?? data.COMMENTS ?? null;
    commentEditsRef.current = recordCommentEdit(commentEditsRef.current, data, params.oldValue, value);
    setHasUnsavedChanges(commentEditsRef.current.size > 0);
    data.COMMENTS = value;
    if (api?.getRowNode) {
      const rowNode = api.getRowNode(getReconRowId(data));
      if (rowNode) api.refreshCells({ rowNodes: [rowNode], columns: ['COMMENTS'], force: true });
    }
    // Rows sharing a comment key share one saved comment, so they show one draft.
    const key = commentKey(data);
    setRowData(rows => rows.map(row => (
      row === data || commentKey(row) === key ? { ...row, COMMENTS: value } : row
    )));
  }, []);

  const validateSave = useCallback(() => {
    const error_entries = [];
    // Only rows carrying a comment are persisted (the backend diffs comments),
    // so validate just those. CW_ACCT_ID is legitimately empty for custody-only
    // (one-sided) breaks and the comment join is null-safe on it, so it isn't
    // required to save a comment — otherwise every custody-only row would block
    // the save with a false "empty cols: [CW_ACCT_ID]" error.
    const requiredForComment = config.validation.requiredFields.filter(
      col => col !== 'CW_ACCT_ID'
    );
    rowData.forEach((entry, idx) => {
      if (!commentEditsRef.current.has(commentKey(entry)) || isValueNull(entry.COMMENTS)) return;
      const empty_cols = requiredForComment.filter(col => isValueNull(entry[col]));
      if (empty_cols.length !== 0) {
        error_entries.push({ 'row': idx, 'account': entry['ACCOUNT'], 'empty cols': empty_cols });
      }
    });
    if (error_entries.length === 0) return "";
    return "Validation Error\n" + error_entries.map(e => JSON.stringify(e)).join("\n");
  }, [rowData]);

  const handleSave = useCallback(async () => {
    const error_message = validateSave();
    if (error_message.length !== 0) {
      showNotification(error_message, 'error');
    } else {
      try {
        setIsSaving(true);
        const sent = commentEditsRef.current;
        const filteredData = buildCommentWrites(rowData, sent);
        if (filteredData.length === 0) {
          setHasUnsavedChanges(false);
          return;
        }
        const resp = await saveReconEntries(filteredData);
        commentEditsRef.current = removeSavedEdits(commentEditsRef.current, sent);
        await loadEntries({ preserveEdits: true });
        showNotification(`Successfully saved ${resp.data.records_saved} recon entries`, 'success');
        if (onSaveSuccess) onSaveSuccess();
      } catch (error) {
        console.error("Error saving:", error);
        if (!notifyPermissionError(showNotification, error)) {
          showNotification(
            error instanceof DuplicateCommentRowsError
              ? error.message
              : getWriteErrorMessage(error, "Failed to save recon entries. Please try again."),
            'error'
          );
          if (shouldReloadAfterWriteError(error)) await loadEntries({ preserveEdits: true });
        }
      } finally {
        setIsSaving(false);
      }
    }
  }, [rowData, validateSave, showNotification, loadEntries, onSaveSuccess]);

  const handleExcludeClick = useCallback(() => {
    if (selectedCount === 0) {
      showNotification('Please select at least one row to exclude', 'warning');
      return;
    }
    // The backend excludes by key, so every loaded row sharing a selected key
    // is excluded too; say how many.
    const keys = new Set(getSelectedData(rowData).map(row => exclusionKey(row, selectedDate)));
    setExclusionCoverage(rowData.filter(row => keys.has(exclusionKey(row, selectedDate))).length);
    setExclusionReason('');
    setExcludeDialogOpen(true);
  }, [selectedCount, showNotification, getSelectedData, rowData, selectedDate]);

  const handleExcludeConfirm = useCallback(async () => {
    if (!exclusionReason.trim()) {
      showNotification('Please provide an exclusion reason', 'warning');
      return;
    }
    try {
      setIsExcluding(true);
      setExcludeDialogOpen(false);
      // One write per exclusion key: the writer rejects a batch with a key twice.
      const byKey = new Map();
      getSelectedData(rowData).forEach(row => {
        const key = exclusionKey(row, selectedDate);
        if (!byKey.has(key)) byKey.set(key, row);
      });
      const exclusions = [...byKey.values()].map(row => ({
        REC_CUST: row.REC_CUST,
        REC_SRC_ACCT: row.REC_SRC_ACCT,
        REC_SEC_ID: row.REC_SEC_ID,
        CU_SEC_ID: row.CU_SEC_ID,
        REC_ASOFDT: row.REC_ASOFDT || selectedDate,
        CW_ACCT_ID: row.CW_ACCT_ID,
        EXCLUDED_REASON: exclusionReason.trim(),
        // A new exclusion (or re-exclusion over a deleted one) is created from the recon grid.
        MODIFIED_DATE: null
      }));
      await saveExclusions(exclusions);
      clearSelection();
      setExclusionReason('');
      await loadEntries({ preserveEdits: true });
      if (onExclusionSuccess) onExclusionSuccess();
      showNotification(`Successfully excluded ${exclusions.length} position(s)`, 'success');
    } catch (error) {
      console.error("Error excluding positions:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(getWriteErrorMessage(error, "Failed to exclude positions. Please try again."), 'error');
        if (shouldReloadAfterWriteError(error)) await loadEntries({ preserveEdits: true });
      }
    } finally {
      setIsExcluding(false);
    }
  }, [rowData, selectedDate, exclusionReason, loadEntries, showNotification, onExclusionSuccess, clearSelection, getSelectedData]);

  // Summary statistics using 1823-core, computed over the FULL dataset (not the
  // breaks-only view) so the bar shows the date's true totals even while the grid
  // is filtered. This desk reconciles on UNITS, so breaks are described by the
  // orig/curr units alerts (the same values driving the grid's orange/red cells)
  // plus one-sided NOT MATCHING positions. FV/BV value breaks are not counted.
  // See utils/breakStats.
  // useSummaryStats does not call `calculate` for an empty grid: it hands `format` a literal 0.
  // So "no rows" has to be told apart in `format` itself, or a date where every row breaks (a
  // real 0%) would read as "N/A", the same as a date with no data.
  const hasRows = Array.isArray(rowData) && rowData.length > 0;
  const calculations = useMemo(() => ({
    totalRows: { label: 'Total Records', calculate: (data) => data.length, color: 'primary' },
    origUnitBreaks: { label: 'Orig Unit Breaks', calculate: (data) => countWhere(data, isOrigUnitBreak), color: 'warning' },
    currUnitBreaks: { label: 'Curr Unit Breaks', calculate: (data) => countWhere(data, isCurrUnitBreak), color: 'error' },
    persistentBreaks: { label: 'Persistent (3M+)', calculate: (data) => countWhere(data, isPersistentBreak), color: 'error' },
    notMatching: { label: 'Not Matching', calculate: (data) => countWhere(data, isNotMatching), color: 'warning' },
    matchRate: {
      label: 'Match Rate',
      calculate: (data) => {
        if (data.length === 0) return 0;
        return ((data.length - countBreaks(data)) / data.length) * 100;
      },
      format: (val) => (hasRows ? `${val.toFixed(1)}%` : 'N/A'),
      color: 'success',
    },
  }), [hasRows]);
  const { stats: summaryStats } = useSummaryStats({ rowData, calculations });

  // The grid view honors the Breaks Only toggle; the summary bar above still
  // sees the full rowData. filterBreaks preserves row object refs, so inline
  // comment edits and selection continue to operate on the same objects.
  const breakCount = useMemo(() => countBreaks(rowData), [rowData]);
  const displayedRowData = useMemo(
    () => (showBreaksOnly ? filterBreaks(rowData) : rowData),
    [rowData, showBreaksOnly]
  );

  // Owning the hook here (rather than letting GridConfigMenu instantiate
  // it internally) lets us wire gridConfigs.onGridReady to PamGrid's
  // onGridReady prop — without that bridge, a slow grid that mounts AFTER
  // the configs response would miss the default-layout auto-apply.
  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'recon-entries',
    gridApiRef: gridRef,
  });

  // GridConfigMenu is stateful (dropdown + dialog) and needs gridRef, so
  // it goes through PamGrid's customToolbarComponents slot rather than the
  // button-config-only customToolbarButtons array.
  const customToolbarComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Custom toolbar buttons. Exclude stays first (index 0) — existing tests and
  // muscle memory target it there; the Breaks Only view toggle follows it.
  const customToolbarButtons = useMemo(() => [
    {
      label: isExcluding ? "Excluding..." : "Exclude",
      onClick: handleExcludeClick,
      disabled: !!EXCLUSIONS_UNAVAILABLE || !canExclude || selectedCount === 0 || isExcluding || isSaving,
      // The permission reason wins over the selection hint: with no grant, selecting rows will not
      // help, so telling someone to select rows would send them down a dead end. An unavailable
      // backend wins over both.
      tooltip: EXCLUSIONS_UNAVAILABLE || (!canExclude
        ? 'You do not have permission to exclude positions — request the recon_editor role from the permissions menu'
        : selectedCount > 0 ? `Exclude ${selectedCount} selected position(s)` : 'Select rows to exclude'),
      sx: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        backgroundColor: selectedCount > 0 ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
        border: `1px solid ${theme.palette.error.main}`,
        '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.2)' },
        '&:disabled': { color: theme.palette.grey[400], borderColor: theme.palette.grey[300], backgroundColor: 'transparent' }
      }
    },
    {
      label: showBreaksOnly ? `Breaks Only (${breakCount})` : "Breaks Only",
      onClick: () => setShowBreaksOnly((v) => !v),
      disabled: breakCount === 0 && !showBreaksOnly,
      tooltip: showBreaksOnly
        ? 'Showing breaking positions only — click to show all'
        : `Filter to the ${breakCount} breaking position(s)`,
      sx: {
        color: showBreaksOnly ? theme.palette.common.white : theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
        backgroundColor: showBreaksOnly ? theme.palette.warning.main : 'transparent',
        border: `1px solid ${theme.palette.warning.main}`,
        '&:hover': {
          backgroundColor: showBreaksOnly ? theme.palette.warning.dark : 'rgba(255, 167, 38, 0.15)',
        },
        '&:disabled': { color: theme.palette.grey[400], borderColor: theme.palette.grey[300] },
      },
    },
    // Only after a failed reload: the way back to editable comments.
    ...(rowsStale ? [{
      label: isLoading ? 'Reloading...' : 'Reload',
      onClick: () => loadEntries({ preserveEdits: true }),
      disabled: isLoading,
      tooltip: 'The last reload failed, so these rows may be out of date. Comments stay read-only until a reload succeeds.',
      sx: {
        color: theme.palette.info.main,
        borderColor: theme.palette.info.main,
        border: `1px solid ${theme.palette.info.main}`,
        '&:disabled': { color: theme.palette.grey[400], borderColor: theme.palette.grey[300] },
      },
    }] : []),
  ], [EXCLUSIONS_UNAVAILABLE, canExclude, handleExcludeClick, selectedCount, isExcluding, isSaving, theme, showBreaksOnly, breakCount, rowsStale, isLoading, loadEntries]);

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
      {(COMMENTS_UNAVAILABLE || EXCLUSIONS_UNAVAILABLE) && (
        <Alert severity="info" sx={{ mb: 1 }} data-testid="recon-writes-unavailable">
          {[COMMENTS_UNAVAILABLE, EXCLUSIONS_UNAVAILABLE].filter(Boolean).join(' ')}
        </Alert>
      )}
      <SummaryStatsBar stats={summaryStats} />
      <PamGrid
        ref={gridRef}
        rowData={displayedRowData}
        columnDefs={columnDefs}
        getRowId={getReconRowId}
        height={`${height - 200}px`}
        // Toolbar
        showToolbar
        title="Fixed Income Recon"
        showDateSelect
        availableDates={availableDates}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        isLoadingDates={isLoadingDates}
        showExport
        exportConfig={{
          fileName: `FixedIncomeRecon_${selectedDate || new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Fixed Income Recon',
        }}
        showSave={!COMMENTS_UNAVAILABLE}
        onSave={handleSave}
        changedRowCount={hasUnsavedChanges ? 1 : 0}
        isSaving={isSaving}
        customToolbarButtons={customToolbarButtons}
        customToolbarComponents={customToolbarComponents}
        onGridReady={gridConfigs.onGridReady}
        // Mirror every column/sort/filter gesture into the local stash so
        // unsaved tweaks survive a page refresh. PamGrid surfaces
        // onFilterChanged at the top level; the column events ride through
        // agGridProps as the stashBindings bundle.
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={{
          ...gridConfigs.stashBindings,
          onCellEditingStarted,
          onCellEditingStopped,
        }}
        // Selection column
        showSelectionColumn
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        isSelectionDisabled={isSaving || isExcluding}
        // Quick Filter
        showQuickFilter
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter..."
        // State
        isLoading={isLoading}
        loadingMessage="Loading recon entries..."
        // Grid events
        onCellValueChanged={onCellValueChanged}
        // Additional modules
        modules={[SetFilterModule]}
        // Legend
        showLegend
        legendItems={[
          { color: 'rgba(255, 165, 0, 0.3)', border: '1px solid rgba(255, 165, 0, 0.5)', label: 'Orange: Orig/Curr Units Diff alert 1-2 months' },
          { color: 'rgba(255, 0, 0, 0.3)', border: '1px solid rgba(255, 0, 0, 0.5)', label: 'Red: Orig/Curr Units Diff alert 3+ months' },
        ]}
      />

      {/* Exclusion Reason Dialog */}
      <ConfirmationDialog
        open={excludeDialogOpen}
        onClose={() => { setExcludeDialogOpen(false); setExclusionReason(''); }}
        onConfirm={handleExcludeConfirm}
        title="Exclude Positions"
        confirmText={isExcluding ? "Excluding..." : "Exclude"}
        confirmColor="error"
        message={
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You are about to exclude {exclusionCoverage || selectedCount} position(s). Please provide a reason for exclusion.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={4}
              label="Exclusion Reason"
              placeholder="Enter reason for exclusion..."
              value={exclusionReason}
              onChange={(e) => setExclusionReason(e.target.value)}
              variant="outlined"
              required
            />
          </Box>
        }
      />
    </Box>
  );
};

export default ReconList;
